// The Royale beyond the match: your tally (played, wins, best placing), the win
// rewards you may open (core royale-rewards.js), and a guest's result kept until
// they sign in. Talks to the edge-server's /api/royale routes
// (apps/edge-server/src/royale/rewards.js) and reads the public tally table for
// the leaderboard.
import { useEffect, useState } from "react";
import { supabase } from "./supabase";
import { dsHttpOrigin } from "../online/net";
import { CROWN_REWARDS, crownReward, nextCrownReward } from "core/src/royale-rewards.js";
import { refreshEggBalance, setEggBalance } from "./shop/useEggBalance";

export interface CrownReward { id: string; wins: number; name: string; eggs: number; item?: { kind: string; id: string } }
export const ROYALE_REWARDS: CrownReward[] = CROWN_REWARDS as any;
export const royaleReward = crownReward as (id: string) => CrownReward | null;
export const nextRoyaleReward = nextCrownReward as (wins: number) => CrownReward | null;

export interface RoyaleMe { wins: number; played: number; bestPlace: number | null; opened: string[]; openable: string[] }

async function accessToken(): Promise<string | null> {
  if (!supabase) return null;
  try {
    const { data } = await supabase.auth.getSession();
    return data.session?.access_token ?? null;
  } catch {
    return null;
  }
}
async function call(path: string, body?: unknown): Promise<any> {
  const token = await accessToken();
  if (!token) return { error: "not_signed_in" };
  try {
    const res = await fetch(`${dsHttpOrigin()}/api/royale/${path}`, {
      method: body === undefined ? "GET" : "POST",
      headers: { authorization: `Bearer ${token}`, ...(body === undefined ? {} : { "content-type": "application/json" }) },
      body: body === undefined ? undefined : JSON.stringify(body),
    });
    return await res.json();
  } catch {
    return { error: "network" };
  }
}

// The signed-in player's Royale tally and rewards; null when signed out or unavailable.
export async function fetchRoyaleMe(): Promise<RoyaleMe | null> {
  const r = await call("me");
  return r && !r.error && typeof r.wins === "number" ? r : null;
}

// Open (claim) an earned win reward. Resolves to what it gave, or { error }.
// `keepBalance`: the caller flies the eggs in and sets the balance itself.
export async function openRoyaleReward(id: string, keepBalance = false): Promise<{ opened?: string; eggs?: number; item?: { kind: string; id: string } | null; balance?: number | null; error?: string }> {
  const r = await call("open", { id });
  if (r && !r.error) {
    if (!keepBalance) { if (typeof r.balance === "number") setEggBalance(r.balance); else refreshEggBalance(); }
    dropClaimable(id);
  }
  return r || { error: "network" };
}

// ---- the rewards waiting to be claimed (in the Store) ----
// A win reward reached is claimed in the Store (the win screen shows it and
// sends you there): the Store's claim strip and the navbar's count on Store
// read this. Refreshed on sign-in and whenever a tally is read.
let claimable: CrownReward[] = [];
const claimSubs = new Set<(c: CrownReward[]) => void>();
const emitClaimable = () => claimSubs.forEach((f) => f(claimable));
export function setRoyaleTally(me: RoyaleMe | null): void {
  claimable = me ? (me.openable.map(royaleReward).filter(Boolean) as CrownReward[]) : [];
  emitClaimable();
}
function dropClaimable(id: string): void {
  if (!claimable.some((r) => r.id === id)) return;
  claimable = claimable.filter((r) => r.id !== id);
  emitClaimable();
}
let claimStarted = false;
function startClaimable() {
  if (claimStarted || !supabase) return;
  claimStarted = true;
  const read = () => { void fetchRoyaleMe().then(setRoyaleTally); };
  read();
  supabase.auth.onAuthStateChange((event) => { if (event === "SIGNED_IN" || event === "SIGNED_OUT") read(); });
}
export function useRoyaleClaimable(): CrownReward[] {
  const [list, setList] = useState(claimable);
  useEffect(() => {
    startClaimable();
    claimSubs.add(setList);
    setList(claimable);
    return () => { claimSubs.delete(setList); };
  }, []);
  return list;
}

// ---- a guest's result, kept until they sign in ----
// A guest's Royale result arrives as a signed token (the server's "royale_claim"
// message). It waits in this tab; once the player is signed in it is sent back
// and recorded under their account.
const GUEST_KEY = "ar.royaleGuestClaim";
export function keepGuestRoyaleResult(token: string, place: number): void {
  try { sessionStorage.setItem(GUEST_KEY, JSON.stringify({ token, place })); } catch { /* no storage */ }
}
export function heldGuestRoyaleResult(): { token: string; place: number } | null {
  try { return JSON.parse(sessionStorage.getItem(GUEST_KEY) || "null"); } catch { return null; }
}
export async function claimGuestRoyaleResult(): Promise<boolean> {
  const held = heldGuestRoyaleResult();
  if (!held) return false;
  const r = await call("claim-guest", { token: held.token });
  if (r && r.error === "not_signed_in") return false; // keep it: still a guest
  try { sessionStorage.removeItem(GUEST_KEY); } catch { /* no storage */ }
  return !!(r && r.recorded);
}

// ---- the leaderboard's Royale wins ----
export interface RoyaleLeader { userId: string; name: string; flag: string | null; wins: number; played: number; bestPlace: number | null }
export async function fetchRoyaleLeaders(limit = 100): Promise<RoyaleLeader[]> {
  if (!supabase) return [];
  try {
    const { data, error } = (await supabase
      .from("royale_stats")
      .select("user_id, display_name, flag, wins, played, best_place")
      .gt("wins", 0)
      .order("wins", { ascending: false })
      .order("played", { ascending: true })
      .limit(limit)) as { data: any[] | null; error: unknown };
    if (error || !data) return [];
    return data.map((r) => ({ userId: r.user_id, name: r.display_name, flag: r.flag, wins: r.wins, played: r.played, bestPlace: r.best_place }));
  } catch {
    return [];
  }
}

// Your own Royale history, newest first (for the profile).
export interface RoyaleHistoryRow { number: number; place: number; size: number; at: string }
export async function fetchRoyaleHistory(limit = 20): Promise<RoyaleHistoryRow[]> {
  if (!supabase) return [];
  try {
    const { data, error } = (await supabase
      .from("royale_results")
      .select("royale_number, place, field_size, created_at")
      .order("created_at", { ascending: false })
      .limit(limit)) as { data: any[] | null; error: unknown };
    if (error || !data) return [];
    return data.map((r) => ({ number: r.royale_number, place: r.place, size: r.field_size, at: r.created_at }));
  } catch {
    return [];
  }
}
